/**
 * Cognito Authentication Service for Frontend
 */

import { CognitoUserPool, CognitoUser, AuthenticationDetails, CognitoUserAttribute } from 'amazon-cognito-identity-js';

// Get configuration from environment variables
const USER_POOL_ID = import.meta.env?.VITE_COGNITO_USER_POOL_ID || '';
const USER_POOL_CLIENT_ID = import.meta.env?.VITE_COGNITO_USER_POOL_CLIENT_ID || '';
const REGION = import.meta.env?.VITE_AWS_REGION || 'us-east-1';
const API_BASE = import.meta.env?.VITE_API_URL || '/api';

let userPool: CognitoUserPool | null = null;

function getUserPool(): CognitoUserPool {
  if (!userPool) {
    if (!USER_POOL_ID || !USER_POOL_CLIENT_ID) {
      throw new Error('Cognito User Pool ID and Client ID must be configured');
    }
    userPool = new CognitoUserPool({
      UserPoolId: USER_POOL_ID,
      ClientId: USER_POOL_CLIENT_ID,
    });
  }
  return userPool;
}

export interface AuthUser {
  userId: string;
  email: string;
  name?: string;
  groups?: string[];
  attributes?: Record<string, string>;
}

export interface AuthTokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
}

class AuthService {
  private currentUser: CognitoUser | null = null;

  /**
   * Sign in with email and password
   */
  async signIn(email: string, password: string): Promise<AuthTokens> {
    return new Promise((resolve, reject) => {
      const authenticationDetails = new AuthenticationDetails({
        Username: email,
        Password: password,
      });

      const user = new CognitoUser({
        Username: email,
        Pool: getUserPool(),
      });

      user.authenticateUser(authenticationDetails, {
        onSuccess: async (result) => {
          this.currentUser = user;
          const tokens = {
            idToken: result.getIdToken().getJwtToken(),
            accessToken: result.getAccessToken().getJwtToken(),
            refreshToken: result.getRefreshToken().getToken(),
          };
          resolve(tokens);
          // Fire-and-forget: acquire gated docs cookies after login
          this.acquireDocsAccessCookies();
        },
        onFailure: (err) => {
          reject(new Error(err.message || 'Authentication failed'));
        },
        newPasswordRequired: () => {
          // Handle new password required flow
          reject(new Error('New password required'));
        },
      });
    });
  }

  /**
   * Sign out
   */
  async signOut(): Promise<void> {
    // Revoke gated docs cookies before clearing tokens
    await this.revokeDocsAccessCookies();

    const pool = getUserPool();
    const user = pool.getCurrentUser();
    
    if (user) {
      user.signOut();
    }
    
    if (this.currentUser) {
      this.currentUser.signOut();
      this.currentUser = null;
    }
    
    // Clear all Cognito tokens from localStorage
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith('CognitoIdentityServiceProvider.')) {
        localStorage.removeItem(key);
      }
    });
    
    // Clear our custom token storage
    localStorage.removeItem('cognito_id_token');
    localStorage.removeItem('cognito_access_token');
    localStorage.removeItem('cognito_refresh_token');
  }

  /**
   * Get current user
   */
  async getCurrentUser(): Promise<AuthUser | null> {
    return new Promise((resolve) => {
      const pool = getUserPool();
      const user = pool.getCurrentUser();

      if (!user) {
        resolve(null);
        return;
      }

      user.getSession((err: Error | null, session: { isValid: () => boolean; getIdToken: () => { getJwtToken: () => string }; getAccessToken: () => { getJwtToken: () => string }; getRefreshToken: () => { getToken: () => string } } | null) => {
        if (err || !session || !session.isValid()) {
          resolve(null);
          return;
        }

        user.getUserAttributes((attrErr: Error | null, attributes: CognitoUserAttribute[] | undefined) => {
          if (err) {
            resolve(null);
            return;
          }

          const userAttrs: Record<string, string> = {};
          attributes?.forEach((attr) => {
            userAttrs[attr.getName()] = attr.getValue();
          });

          const groups = session.getIdToken().payload['cognito:groups'] || [];

          resolve({
            userId: session.getIdToken().payload.sub,
            email: userAttrs.email || '',
            name: userAttrs.name || userAttrs['custom:name'] || '',
            groups: groups as string[],
            attributes: userAttrs,
          });
        });
      });
    });
  }

  /**
   * Get current session tokens
   */
  async getTokens(): Promise<AuthTokens | null> {
    return new Promise((resolve) => {
      const pool = getUserPool();
      const user = pool.getCurrentUser();

      if (!user) {
        resolve(null);
        return;
      }

      user.getSession((err, session) => {
        if (err || !session || !session.isValid()) {
          resolve(null);
          return;
        }

        resolve({
          idToken: session.getIdToken().getJwtToken(),
          accessToken: session.getAccessToken().getJwtToken(),
          refreshToken: session.getRefreshToken().getToken(),
        });
      });
    });
  }

  /**
   * Refresh tokens
   */
  async refreshTokens(): Promise<AuthTokens> {
    return new Promise((resolve, reject) => {
      const pool = getUserPool();
      const user = pool.getCurrentUser();

      if (!user) {
        reject(new Error('No user session'));
        return;
      }

      user.getSession((err, session) => {
        if (err) {
          reject(err);
          return;
        }

        session.getRefreshToken().getToken();
        user.refreshSession(session.getRefreshToken(), (err, session) => {
          if (err) {
            reject(err);
            return;
          }

          const tokens = {
            idToken: session.getIdToken().getJwtToken(),
            accessToken: session.getAccessToken().getJwtToken(),
            refreshToken: session.getRefreshToken().getToken(),
          };
          resolve(tokens);
          // Refresh gated docs cookies alongside token refresh
          this.acquireDocsAccessCookies();
        });
      });
    });
  }

  /**
   * Sign up new user (admin only)
   */
  async signUp(email: string, password: string, attributes?: Record<string, string>): Promise<void> {
    return new Promise((resolve, reject) => {
      const attributeList: CognitoUserAttribute[] = [];

      if (attributes) {
        Object.entries(attributes).forEach(([key, value]) => {
          attributeList.push(new CognitoUserAttribute({ Name: key, Value: value }));
        });
      }

      getUserPool().signUp(email, password, attributeList, [], (err, _result) => {
        if (err) {
          reject(new Error(err.message || 'Sign up failed'));
          return;
        }
        resolve();
      });
    });
  }

  /**
   * Initiate forgot password flow - sends verification code to user's email
   */
  async forgotPassword(email: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const user = new CognitoUser({
        Username: email,
        Pool: getUserPool(),
      });

      user.forgotPassword({
        onSuccess: () => {
          resolve();
        },
        onFailure: (err) => {
          reject(new Error(err.message || 'Failed to initiate password reset'));
        },
      });
    });
  }

  /**
   * Confirm forgot password with verification code and new password
   */
  async confirmForgotPassword(email: string, code: string, newPassword: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const user = new CognitoUser({
        Username: email,
        Pool: getUserPool(),
      });

      user.confirmPassword(code, newPassword, {
        onSuccess: () => {
          resolve();
        },
        onFailure: (err) => {
          reject(new Error(err.message || 'Failed to reset password'));
        },
      });
    });
  }

  /**
   * Acquire CloudFront signed cookies for gated documentation access.
   * Uses a dedicated fetch with credentials: 'include' so the browser
   * processes Set-Cookie headers from the cross-origin API response.
   * Called automatically after sign-in and on token refresh.
   */
  async acquireDocsAccessCookies(): Promise<void> {
    try {
      const tokens = await this.getTokens();
      if (!tokens?.idToken) return;

      const response = await fetch(`${API_BASE}/v1/auth/docs-access`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokens.idToken}`,
        },
      });

      if (!response.ok) {
        console.warn('Failed to acquire docs access cookies:', response.status);
      }
    } catch {
      console.warn('Docs access cookie acquisition failed (non-critical)');
    }
  }

  /**
   * Revoke CloudFront signed cookies for gated documentation.
   * Called on sign-out to clear cross-domain cookies.
   */
  async revokeDocsAccessCookies(): Promise<void> {
    try {
      const tokens = await this.getTokens();
      if (!tokens?.idToken) return;

      await fetch(`${API_BASE}/v1/auth/docs-access/revoke`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokens.idToken}`,
        },
      });
    } catch {
      // Non-critical: cookies will expire naturally
    }
  }

  /**
   * Initiate Google OAuth sign-in
   */
  initiateGoogleSignIn(): void {
    const domain = import.meta.env.VITE_COGNITO_DOMAIN || '';
    const redirectUri = encodeURIComponent(window.location.origin);
    const clientId = USER_POOL_CLIENT_ID;
    const responseType = 'code';
    const scope = 'email openid profile';

    const url = `https://${domain}.auth.${REGION}.amazoncognito.com/oauth2/authorize?client_id=${clientId}&response_type=${responseType}&scope=${scope}&redirect_uri=${redirectUri}`;

    window.location.href = url;
  }
}

export const authService = new AuthService();
